import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ensorTower from "../../images/ensortower.webp";
import useLogin from "../../api/login";
import useParking from "../../api/parking";
import { useTranslation } from 'react-i18next';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    parkingSpotId: "", // Optional parking spot selection
  });
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [loadingParkingSpots, setLoadingParkingSpots] = useState(false);
  
  const loginApi = useLogin();
  const parkingApi = useParking();

  // Load available parking spots
  useEffect(() => {
    const loadParkingSpots = async () => {
      setLoadingParkingSpots(true);
      try {
        const response = await parkingApi.getAllParkingSpots();
        console.log("All parking spots:", response);
        // The response is an object with spots array, not a direct array
        const allSpots = response.spots || response;
        // Filter only spots that don't have an owner
        const availableSpots = allSpots.filter(spot => !spot.owner_id);
        console.log("Available parking spots:", availableSpots);
        setParkingSpots(availableSpots);
      } catch (error) {
        console.error("Error loading parking spots:", error);
      } finally {
        setLoadingParkingSpots(false);
      }
    };

    loadParkingSpots();
  }, []);

  // Generate username when firstName or lastName changes
  useEffect(() => {
    if (formData.firstName && formData.lastName) {
      const generatedUsername = `${formData.firstName}.${formData.lastName}`
        .toLowerCase()
        .replace(/\s+/g, '');
      setUsername(generatedUsername);
    }
  }, [formData.firstName, formData.lastName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsNotMatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      // Create registration request
      const registerRequestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: username,
        email: formData.email,
        password: formData.password,
        parkingSpotId: formData.parkingSpotId || null, // Optional parking spot
      };

        const response = await loginApi.registerRequest(registerRequestData);
        if (response.status !== 201) {
            throw new Error("Registration failed");
        }

        // Handle successful registration
        setSuccess(true);
      
    } catch (error) {
      console.error("Registration error:", error);
      setError(
        error.response?.data?.message || 
        t('auth.registrationFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `url(${ensorTower})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50" />
      
      <div className="relative w-full max-w-md mx-auto px-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg p-8 shadow-xl">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <div className="mx-auto w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
              <span className="text-white text-xl font-bold">E</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.createAccount')}</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('auth.registerPrompt')}</p>
          </div>

          {/* Success message */}
          {success && (
            <div className="p-3 mb-6 text-sm text-green-700 bg-green-100 border border-green-200 rounded-md text-center">
              {t('auth.registrationSuccess')}
              <br />{t('auth.approvalEmail')}
            </div>
          )}

          {/* Registration Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-3 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md text-center">
                {error}
              </div>
            )}
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('auth.firstName')}
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                            dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                            focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={t('auth.firstNamePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('auth.lastName')}
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                            dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                            focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={t('auth.lastNamePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('auth.usernameAuto')}
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 
                          dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                          focus:ring-2 focus:ring-primary focus:border-transparent cursor-not-allowed"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('auth.emailAddress')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                          dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                          focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t('auth.emailRegisterPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('auth.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                          dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                          focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                          dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                          focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>

              {/* Optional parking spot selection */}
              <div>
                <label htmlFor="parkingSpotId" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('auth.parkingSpot')} ({t('auth.optional')})
                </label>
                {loadingParkingSpots ? (
                  <div className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                          dark:border-gray-700 rounded-md text-gray-500 dark:text-gray-400">
                    {t('loading')}...
                  </div>
                ) : (
                  <select
                    id="parkingSpotId"
                    name="parkingSpotId"
                    value={formData.parkingSpotId}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 
                            dark:border-gray-700 rounded-md text-gray-900 dark:text-white placeholder-gray-400
                            focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">{t('auth.noParkingSpot')}</option>
                    {parkingSpots.length > 0 ? (
                      parkingSpots.map((spot) => (
                        <option key={spot.id} value={spot.id}>
                          Plek {spot.spot_number} - Verdieping {spot.floor} ({spot.type})
                        </option>
                      ))
                    ) : (
                      <option disabled>{t('auth.noParkingSpotsAvailable')}</option>
                    )}
                  </select>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className={`w-full flex justify-center py-3 px-4 rounded-md text-sm font-medium text-white
                      bg-primary hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 
                      focus:ring-offset-2 focus:ring-primary ${(loading || success) ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('auth.processing')}
                </span>
              ) : success ? (
                t('auth.registrationSubmitted')
              ) : (
                t('auth.register')
              )}
            </button>
          </form>
          
          {/* Login link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.alreadyAccount')}{" "}
              <Link to="/login" className="font-medium text-primary hover:text-primary-dark transition-colors">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;